/**
 * Парсер plain-текста оферты в структурированные блоки для
 * красивого рендеринга. Ожидаемый формат исходника:
 *
 *   =========================...===========
 *   LUNA PROTOKOL
 *   ПУБЛИЧНАЯ ОФЕРТА ...
 *   =========================...===========
 *   [intro paragraphs]
 *
 *   =========================...===========
 *   1. ОСНОВНЫЕ ПОНЯТИЯ
 *   =========================...===========
 *
 *   1.1. Текст пункта...
 *   1.2. ...
 *
 *   =========================...===========
 *   ПОДТВЕРЖДЕНИЕ ПРИНЯТИЯ УСЛОВИЙ
 *   =========================...===========
 *   ...
 */

export type OfertaBlock =
  | { kind: 'hero'; title: string; subtitle: string; intro: string[] }
  | { kind: 'section'; num: string | null; title: string; paragraphs: Paragraph[] }
  | { kind: 'ack'; title: string; bullets: string[]; signature: string };

export interface Paragraph {
  /** '1.1.' или null — для обычных абзацев без нумерации. */
  num: string | null;
  /** Линии текста внутри абзаца (после лидирующей нумерации, если есть). */
  text: string;
  /** Буллет-items («а)…», «— …», «☑ …»). */
  bullets: string[];
}

const SEPARATOR_RE = /^=+\s*$/;

export function parseOferta(raw: string): OfertaBlock[] {
  const lines = raw.replace(/\r/g, '').split('\n');

  // Разбиваем на группы между строками-сепараторами ===.
  const groups: string[][] = [];
  let cur: string[] = [];
  for (const ln of lines) {
    if (SEPARATOR_RE.test(ln)) {
      if (cur.length > 0) {
        groups.push(cur);
        cur = [];
      }
      continue;
    }
    cur.push(ln);
  }
  if (cur.length > 0) groups.push(cur);

  // Удаляем пустые группы
  const cleaned = groups
    .map((g) => g.join('\n').trim())
    .filter((s) => s.length > 0);

  const blocks: OfertaBlock[] = [];

  // Первая группа = hero (название + под-название)
  // Вторая группа = intro текст (пока нет отдельных sections)
  // Дальше чередуются: section-title, section-body, section-title, ...
  // Последние группы: «ПОДТВЕРЖДЕНИЕ ПРИНЯТИЯ УСЛОВИЙ» + bullets + signature
  if (cleaned.length === 0) return blocks;

  // HERO
  const heroLines = cleaned[0].split('\n').filter(Boolean);
  const heroTitle = heroLines[0] ?? '';
  const heroSubtitle = heroLines.slice(1).join(' ').trim();

  // Intro — идёт сразу после hero и до первой section-title.
  // Section-title = короткая строка «N. TITLE IN CAPS», состоящая из
  // одной логической линии. Intro может быть несколько абзацев подряд.
  let idx = 1;
  const introParas: string[] = [];
  while (idx < cleaned.length) {
    const group = cleaned[idx];
    if (looksLikeSectionTitle(group)) break;
    const paras = group.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
    introParas.push(...paras);
    idx += 1;
  }
  blocks.push({
    kind: 'hero',
    title: heroTitle,
    subtitle: heroSubtitle,
    intro: introParas,
  });

  // Sections
  while (idx < cleaned.length) {
    const titleGroup = cleaned[idx];
    // Detect ack block (ПОДТВЕРЖДЕНИЕ ПРИНЯТИЯ УСЛОВИЙ)
    if (/подтверждение\s+принятия/i.test(titleGroup)) {
      idx += 1;
      const bodyGroup = cleaned[idx] ?? '';
      const parsed = parseAckBody(bodyGroup);
      blocks.push({
        kind: 'ack',
        title: titleGroup.trim(),
        bullets: parsed.bullets,
        signature: parsed.signature,
      });
      idx += 1;
      continue;
    }

    // Normal section
    const { num, title } = parseSectionTitle(titleGroup);
    idx += 1;
    const bodyGroup = cleaned[idx] ?? '';
    const paragraphs = parseSectionBody(bodyGroup);
    blocks.push({ kind: 'section', num, title, paragraphs });
    idx += 1;
  }

  return blocks;
}

function looksLikeSectionTitle(group: string): boolean {
  const t = group.trim();
  // Section titles — короткие (1 строка после trim) + содержат преимущественно
  // CAPS и/или начинаются с цифры N. или «ПОДТВЕРЖДЕНИЕ».
  if (t.split('\n').length > 2) return false;
  if (/^\d+\.\s+[А-ЯЁA-Z]/.test(t)) return true;
  if (/^[А-ЯЁ][А-ЯЁ\s]+$/.test(t)) return true;
  return false;
}

function parseSectionTitle(raw: string): { num: string | null; title: string } {
  const t = raw.trim();
  const m = /^(\d+)\.\s+(.+)$/.exec(t);
  if (m) return { num: m[1], title: m[2].trim() };
  return { num: null, title: t };
}

function parseSectionBody(raw: string): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  // Разделитель абзацев — пустая строка. Сначала нарезаем.
  const chunks = raw.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);

  for (const chunk of chunks) {
    // Определяем лидирующую нумерацию «N.N.» в начале chunk'а.
    const lines = chunk.split('\n').map((l) => l);
    const leadMatch = /^(\d+\.\d+\.)\s+/.exec(lines[0]);
    const num = leadMatch ? leadMatch[1] : null;
    if (leadMatch) lines[0] = lines[0].slice(leadMatch[0].length);

    // Разделяем на основной текст и буллеты.
    // Буллет = строки с одним из префиксов: `  а) `, `  б)`, `  —`, `  •`.
    const bulletRe = /^\s+(?:[а-яё]\)\s+|—\s+|•\s+)/i;
    const textLines: string[] = [];
    const bullets: string[] = [];
    let inBulletBlock = false;
    let currentBullet: string[] = [];

    const flushBullet = () => {
      if (currentBullet.length > 0) {
        bullets.push(currentBullet.join(' ').replace(/\s+/g, ' ').trim());
        currentBullet = [];
      }
    };

    for (const ln of lines) {
      if (bulletRe.test(ln)) {
        flushBullet();
        inBulletBlock = true;
        currentBullet.push(ln.replace(/^\s+/, ''));
      } else if (inBulletBlock && /^\s{4,}\S/.test(ln)) {
        // continuation of current bullet (indented)
        currentBullet.push(ln.trim());
      } else {
        flushBullet();
        inBulletBlock = false;
        if (ln.trim().length > 0) textLines.push(ln.trim());
      }
    }
    flushBullet();

    paragraphs.push({
      num,
      text: textLines.join(' ').replace(/\s+/g, ' ').trim(),
      bullets,
    });
  }
  return paragraphs;
}

function parseAckBody(raw: string): { bullets: string[]; signature: string } {
  const lines = raw.split('\n');
  const bullets: string[] = [];
  const tail: string[] = [];
  let seenBullet = false;
  let currentBullet: string[] = [];

  const flush = () => {
    if (currentBullet.length > 0) {
      bullets.push(currentBullet.join(' ').replace(/\s+/g, ' ').trim());
      currentBullet = [];
    }
  };

  for (const ln of lines) {
    const bulletMatch = /^\s*(?:•|☑|-)\s+(.*)/.exec(ln);
    if (bulletMatch) {
      flush();
      seenBullet = true;
      currentBullet.push(bulletMatch[1]);
    } else if (seenBullet && /^\s{2,}\S/.test(ln)) {
      currentBullet.push(ln.trim());
    } else if (seenBullet && ln.trim() === '') {
      flush();
    } else if (seenBullet) {
      // После буллетов всё сваливается в signature
      flush();
      tail.push(ln.trim());
    }
  }
  flush();

  return {
    bullets,
    signature: tail.filter(Boolean).join('\n').trim(),
  };
}
